import { z } from 'zod';

import type { AudienceContent, StalenessWindow } from './artifacts.ts';
import { studyTypeIdSchema } from './study-types.ts';

/**
 * The queue an audience is reconciled on. The API process stores the request and enqueues it; the
 * worker, which is the only process holding a model, answers it.
 */
export const QUEUE_AUDIENCE_DRAFT = 'audience.draft';

/**
 * How often the Audience step re-reads a pending request, and how long it waits before it offers
 * "Try again". Placeholder policy values, the guide's: an audience is reconciled with the panel's
 * catalog in hand, which is read before the call.
 */
export const AUDIENCE_DRAFT_POLL_MS = 1000;
export const AUDIENCE_DRAFT_WAIT_MS = 60_000;

/**
 * What a reconciliation was asked from: the approved guide the audience is built on, the research
 * questions and the chosen Goal above it, the Study Type and template, the confirmed Materials, and
 * the version of the audience it would write over. Every upstream an audience can be waiting on is
 * here, so a settle writes nothing onto inputs that moved.
 */
export const audienceDraftInputsSchema = z.strictObject({
  dg_version: z.int().positive(),
  rqs_version: z.int().positive(),
  goal_version: z.int().positive(),
  goal_option_id: z.uuid(),
  study_type_id: studyTypeIdSchema,
  template_version: z.int().positive(),
  materials_version: z.int().positive(),
  audience_version: z.int().positive(),
});
export type AudienceDraftInputs = z.infer<typeof audienceDraftInputsSchema>;

export const AUDIENCE_DRAFT_STATUSES = ['pending', 'ready', 'unreadable', 'unavailable'] as const;
export type AudienceDraftStatus = (typeof AUDIENCE_DRAFT_STATUSES)[number];

/**
 * The latest request to reconcile the audience.
 *
 * - `pending` — the worker has not answered yet.
 * - `ready` — the worker reconciled the audience, which is the study's current version.
 * - `unreadable` — the model answered, and its answer was not a whole audience the panel can find.
 * - `unavailable` — no answer could be had: no model is configured, the panel's catalog could not
 *   be read, the day's budget is spent, the call failed, or the study moved while it was being
 *   reconciled.
 */
export const audienceDraftRequestSchema = z.strictObject({
  id: z.uuid(),
  status: z.enum(AUDIENCE_DRAFT_STATUSES),
  requested_at: z.iso.datetime(),
  inputs: audienceDraftInputsSchema,
});
export type AudienceDraftRequest = z.infer<typeof audienceDraftRequestSchema>;

export const AUDIENCE_DRAFT_SCHEMA_VERSION = 1;

/**
 * What `studies.audience_draft` holds: the latest reconciliation request, or none. The reconciled
 * audience itself is an audience version, never held here.
 */
export const storedAudienceDraftSchema = z.strictObject({
  schema_version: z.literal(AUDIENCE_DRAFT_SCHEMA_VERSION),
  request: audienceDraftRequestSchema.nullable(),
});
export type StoredAudienceDraft = z.infer<typeof storedAudienceDraftSchema>;

/**
 * The stored request, or nothing. A shape this build cannot read is treated as no request rather
 * than an error: the study's next request replaces it.
 */
export const parseStoredAudienceDraft = (stored: unknown): StoredAudienceDraft | null => {
  if (stored === null || stored === undefined) {
    return null;
  }

  const parsed = storedAudienceDraftSchema.safeParse(stored);

  return parsed.success ? parsed.data : null;
};

/** Whether two sets of inputs describe the same study. */
export const sameAudienceDraftInputs = (
  left: AudienceDraftInputs,
  right: AudienceDraftInputs,
): boolean =>
  left.dg_version === right.dg_version &&
  left.rqs_version === right.rqs_version &&
  left.goal_version === right.goal_version &&
  left.goal_option_id === right.goal_option_id &&
  left.study_type_id === right.study_type_id &&
  left.template_version === right.template_version &&
  left.materials_version === right.materials_version &&
  left.audience_version === right.audience_version;

/** As much of a study's audience as deciding whether to reconcile it reads. */
export interface AudienceDraftFact {
  staleness: readonly StalenessWindow[];
}

/**
 * Whether the study's audience needs reconciling: it exists and is waiting on a change above it —
 * a changed Goal, changed Materials, changed research questions or a changed guide. Reconciling
 * writes the smallest change that answers each, keeping everything it does not need to touch as it
 * is stored. A study with no audience has nothing to reconcile: its first audience is drafted when
 * the guide is approved.
 */
export const audienceReconcileNeeded = (audience: AudienceDraftFact | null): boolean =>
  audience !== null && audience.staleness.length > 0;

/**
 * Whether a study needs a new request for its current inputs, rather than the one it holds.
 *
 * Only while the audience needs reconciling at all, and not while a request for these inputs is
 * pending and younger than `AUDIENCE_DRAFT_WAIT_MS`. A pending request older than that is one the
 * step has given up waiting on — its job was lost with a worker, expired, or never ran — and since
 * the queue retries nothing, treating it as still in hand would leave the step waiting on nothing.
 */
export const audienceDraftRequestNeeded = ({
  request,
  inputs,
  audience,
  now,
}: {
  request: AudienceDraftRequest | null;
  inputs: AudienceDraftInputs;
  audience: AudienceDraftFact | null;
  now: Date;
}): boolean => {
  if (!audienceReconcileNeeded(audience)) {
    return false;
  }

  const waiting =
    request !== null &&
    request.status === 'pending' &&
    sameAudienceDraftInputs(request.inputs, inputs) &&
    now.getTime() - Date.parse(request.requested_at) < AUDIENCE_DRAFT_WAIT_MS;

  return !waiting;
};

/** Why one item of a reconciled audience changed, as the reconciler said; see `ChangeNotesInput`. */
export interface AudienceChangeReason {
  id: string;
  reason: string;
}

/**
 * How a request is settled: every status but the one it starts in. A `ready` reconciliation carries
 * the reconciled audience, its pipeline constraints left for the store to derive, and the reason it
 * gave for each filter, screening question or targeting gap it changed, added or removed.
 */
export type AudienceDraftOutcome =
  | { status: 'ready'; content: AudienceContent; reasons: readonly AudienceChangeReason[] }
  | { status: 'unreadable' }
  | { status: 'unavailable' };

/**
 * The job the API process sends. It names the request as well as the study, so a job for a request
 * a later one has replaced is recognised as stale and spends nothing.
 */
export const audienceDraftPayloadSchema = z.object({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type AudienceDraftPayload = z.infer<typeof audienceDraftPayloadSchema>;
